/**
 * Simple Audio Engine: playback control with Web Audio API
 */

export class AudioEngine {
  private audioContext: AudioContext;
  private masterGainNode: GainNode;
  private trackGainNodes: Map<string, GainNode> = new Map();
  private trackOffsetsMs: Map<string, number> = new Map();
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  private activeSources: Map<string, AudioBufferSourceNode> = new Map();
  
  public isPlaying = false;
  public playheadMs = 0;
  private globalStartTime = 0;
  private initialPlayheadMs = 0;
  private frameId: number | null = null;
  private playbackRunId = 0;

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
    this.masterGainNode = this.audioContext.createGain();
    this.masterGainNode.connect(this.audioContext.destination);
    this.masterGainNode.gain.value = 0.8;
  }

  public async resume(): Promise<void> {
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  public getPlayheadMs(): number {
    return this.playheadMs;
  }

  public getLoadedTrackIds(): string[] {
    return Array.from(this.audioBuffers.keys());
  }

  public async loadBuffer(trackId: string, blob: Blob): Promise<void> {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.audioBuffers.set(trackId, audioBuffer);
    } catch (error) {
      const type = blob.type || "application/octet-stream";
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to decode audio for track ${trackId} (${type}): ${message}`);
    }
  }

  private getTrackGainNode(trackId: string): GainNode {
    if (!this.trackGainNodes.has(trackId)) {
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 1;
      gainNode.connect(this.masterGainNode);
      this.trackGainNodes.set(trackId, gainNode);
    }
    return this.trackGainNodes.get(trackId)!;
  }

  public play(playheadMs: number): void {
    if (this.isPlaying) return;
    
    const runId = ++this.playbackRunId;
    this.isPlaying = true;
    this.initialPlayheadMs = playheadMs;
    this.globalStartTime = this.audioContext.currentTime;
    
    // Schedule all available tracks
    this.audioBuffers.forEach((buffer, trackId) => {
      this.playTrack(trackId, buffer, playheadMs, runId);
    });

    // Update playhead every 50ms
    const updatePlayhead = () => {
      if (this.isPlaying) {
        const elapsed = (this.audioContext.currentTime - this.globalStartTime) * 1000;
        this.playheadMs = this.initialPlayheadMs + elapsed;
        this.frameId = requestAnimationFrame(updatePlayhead);
      }
    };
    updatePlayhead();
  }

  private playTrack(trackId: string, buffer: AudioBuffer, startMs: number, runId: number): void {
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    
    const gainNode = this.getTrackGainNode(trackId);
    source.connect(gainNode);
    
    const trackOffsetMs = this.trackOffsetsMs.get(trackId) || 0;
    const relativeMs = startMs - trackOffsetMs;

    let when = this.audioContext.currentTime;
    let offsetSeconds = 0;

    if (relativeMs >= 0) {
      offsetSeconds = relativeMs / 1000;
    } else {
      when += Math.abs(relativeMs) / 1000;
    }

    const duration = buffer.duration - offsetSeconds;
    if (duration > 0) {
      source.start(when, offsetSeconds);
      this.activeSources.set(trackId, source);
      
      source.onended = () => {
        if (runId !== this.playbackRunId) {
          return;
        }

        if (this.activeSources.get(trackId) === source) {
          this.activeSources.delete(trackId);
        }

        if (this.activeSources.size === 0) {
          this.isPlaying = false;
          if (this.frameId !== null) {
            cancelAnimationFrame(this.frameId);
            this.frameId = null;
          }
        }
      };
    }
  }

  public stop(): void {
    this.playbackRunId += 1;
    this.isPlaying = false;
    
    // Stop all active sources
    this.activeSources.forEach((source) => {
      try {
        source.stop();
      } catch (e) {
        // Source might already be ended
      }
    });
    this.activeSources.clear();
    
    // Cancel animation frame
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  public seek(playheadMs: number): void {
    const wasPlaying = this.isPlaying;
    this.stop();
    this.playheadMs = playheadMs;
    
    if (wasPlaying) {
      this.play(playheadMs);
    }
  }

  public removeTrack(trackId: string): void {
    const source = this.activeSources.get(trackId);
    if (source) {
      try {
        source.stop();
      } catch {
        // ignore stop errors for ended nodes
      }
      this.activeSources.delete(trackId);
    }

    this.audioBuffers.delete(trackId);
    this.trackOffsetsMs.delete(trackId);
    const gainNode = this.trackGainNodes.get(trackId);
    if (gainNode) {
      gainNode.disconnect();
      this.trackGainNodes.delete(trackId);
    }
  }

  public setTrackGain(trackId: string, gain: number): void {
    const gainNode = this.getTrackGainNode(trackId);
    gainNode.gain.setValueAtTime(gain, this.audioContext.currentTime);
  }

  public setTrackOffset(trackId: string, offsetMs: number): void {
    this.trackOffsetsMs.set(trackId, Math.max(0, offsetMs));
  }

  public setMasterGain(gain: number): void {
    this.masterGainNode.gain.setValueAtTime(gain, this.audioContext.currentTime);
  }
}
